import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface ConfirmationDialogOptions {
  headerTitle?: string;
  headerName?: string;
  message?: string;
  content?: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'confirm' | 'warning' | 'danger' | 'info';
  isDefault?: boolean;
  isCustomMsg?: boolean;
  onResult?: (result: boolean) => void;
}

@Injectable({ providedIn: 'root' })
export class ConfirmationDialogService {
  private isOpenSubject = new BehaviorSubject<boolean>(false);
  isOpen$: Observable<boolean> = this.isOpenSubject.asObservable();

  private headerTitleSubject = new BehaviorSubject<string>('');
  headerTitle$ = this.headerTitleSubject.asObservable();

  private headerNameSubject = new BehaviorSubject<string>('');
  headerName$ = this.headerNameSubject.asObservable();

  private messageSubject = new BehaviorSubject<string>('');
  message$ = this.messageSubject.asObservable();

  private contentSubject = new BehaviorSubject<string>('');
  content$ = this.contentSubject.asObservable();

  private isDefaultSubject = new BehaviorSubject<boolean>(true);
  isDefault$ = this.isDefaultSubject.asObservable();

  private isCustomMsgSubject = new BehaviorSubject<boolean>(true);
  isCustomMsg$ = this.isCustomMsgSubject.asObservable();

  private confirmTextSubject = new BehaviorSubject<string>('');
  confirmText$ = this.confirmTextSubject.asObservable();

  private cancelTextSubject = new BehaviorSubject<string>('');
  cancelText$ = this.cancelTextSubject.asObservable();

  private typeSubject = new BehaviorSubject<string>('confirm');
  type$ = this.typeSubject.asObservable();

  private resultCallback: ((result: boolean) => void) | null = null;

  open(options: ConfirmationDialogOptions) {
    const isDefault =
      options.isDefault !== undefined ? options.isDefault : true;
    const isCustomMsg =
      options.isCustomMsg !== undefined ? options.isCustomMsg : !isDefault;

    this.headerTitleSubject.next(options.headerTitle || '');
    this.headerNameSubject.next(options.headerName || 'Confirm');
    this.isDefaultSubject.next(isDefault);
    this.isCustomMsgSubject.next(isCustomMsg);

    if (isDefault) {
      this.messageSubject.next(options.message || '');
      this.contentSubject.next('');
    } else {
      this.messageSubject.next('');
      this.contentSubject.next(options.content || '');
    }

    this.confirmTextSubject.next(options.confirmText || 'Confirm');
    this.cancelTextSubject.next(options.cancelText || 'Cancel');
    this.typeSubject.next(options.type || 'confirm');

    this.resultCallback = options.onResult || null;

    this.isOpenSubject.next(true);
  }

  confirm() {
    if (this.resultCallback) this.resultCallback(true);
    this.close();
  }

  cancel() {
    if (this.resultCallback) this.resultCallback(false);
    this.close();
  }

  close() {
    this.isOpenSubject.next(false);
    this.headerTitleSubject.next('');
    this.headerNameSubject.next('');
    this.messageSubject.next('');
    this.contentSubject.next('');
    this.isDefaultSubject.next(true);
    this.isCustomMsgSubject.next(false);
    this.confirmTextSubject.next('');
    this.cancelTextSubject.next('');
    this.typeSubject.next('confirm');
    this.resultCallback = null;
  }
}
